import { cloneWorkflowGraph } from './cloneWorkflowGraph.js';

const getParentStepId = (stepId, dag = {}) => {
  for (const id in dag) {
    if (dag[id].exit === stepId || dag[id].child === stepId || dag[id].branches?.includes(stepId)) {
      return id;
    }
  }
};

const getExitStepId = (rootId, dag = {}) => {
  const visited = new Set();

  const iterate = (stepId) => {
    if (!stepId || visited.has(stepId)) {
      return stepId;
    }
    visited.add(stepId);

    const step = dag[stepId];
    if (!step?.exit) {
      return stepId;
    }
    return iterate(step.exit);
  };

  return iterate(rootId);
};

const getParentKey = (stepId, parentStepId, dag) => {
  let parentKey = 'exit';
  if (dag[parentStepId]?.branches?.includes(stepId)) {
    parentKey = 'branches';
  } else if (dag[parentStepId]?.child === stepId) {
    parentKey = 'child';
  }
  return parentKey;
};

const setParentReference = ({ stepId, parentStepId, dag, value }) => {
  if (!parentStepId) {
    return;
  }

  const parentKey = getParentKey(stepId, parentStepId, dag);
  if (parentKey === 'branches') {
    const index = dag[parentStepId].branches.indexOf(stepId);
    if (index >= 0) {
      dag[parentStepId].branches[index] = value;
    }
    return;
  }

  dag[parentStepId][parentKey] = value;
};

const getAllDeepChildrenAndBranches = (dag, stepId) => {
  const step = dag[stepId];
  const fullList = [];
  const directChildren = [step?.child, ...(step?.branches || [])].filter((n) => !!n);
  const visited = new Set(directChildren);
  fullList.push(...directChildren);
  const iterate = (childStepId) => {
    if (!childStepId) {
      return;
    }

    const childStep = dag[childStepId];
    const childChildren = [childStep?.exit, childStep?.child, ...(childStep?.branches || [])].filter((n) => !!n && n !== childStepId);
    for (const childId of childChildren) {
      if (visited.has(childId)) {
        continue;
      }
      visited.add(childId);
      fullList.push(childId);
      iterate(childId);
    }
  };

  directChildren.forEach(iterate);
  return [...new Set(fullList)];
};

const deleteStepAndKeepChildren = ({ stepId, parentStepId, dag }) => {
  const step = dag[stepId];
  const replacementStepId = step.child || step.exit;
  const childEndId = step.child ? getExitStepId(step.child, dag) : null;

  setParentReference({ stepId, parentStepId, dag, value: replacementStepId });

  if (childEndId && dag[childEndId]) {
    dag[childEndId].exit = step.exit;
  }

  delete dag[stepId];

  return dag;
};

const deleteFolderStep = ({ stepId, parentStepId, dag }) => {
  return deleteStepAndKeepChildren({ stepId, parentStepId, dag });
};

/**
 * Deletes a normal step from the graph:
 *
 *  - Recursively delete all children
 *  - Reassigns the step's exit to the parent's child or exit
 */
const deleteNormalStep = ({ stepId, parentStepId, dag, shallow = false, keepChildren = false }) => {
  const step = dag[stepId];

  const hasBranches = Array.isArray(step?.branches) && step.branches.some((branchId) => !!branchId);
  if (keepChildren && !hasBranches) {
    return deleteStepAndKeepChildren({ stepId, parentStepId, dag });
  }

  if (shallow === false) {
    const deepChildren = getAllDeepChildrenAndBranches(dag, stepId);
    deepChildren.forEach((n) => {
      delete dag[n];
    });
  }

  // Reassigned the step's exit to the parent's child or exit.
  setParentReference({ stepId, parentStepId, dag, value: step.exit });

  delete dag[stepId];

  return dag;
};

const normalizeDeleteOptions = (options) => {
  if (typeof options === 'boolean') {
    return { shallow: options, keepChildren: false };
  }

  return {
    shallow: options?.shallow === true,
    keepChildren: options?.keepChildren === true
  };
};

export const deleteStepFromGraph = (graph, stepId, options = false) => {
  const { shallow, keepChildren } = normalizeDeleteOptions(options);
  const dag = cloneWorkflowGraph(graph);
  if (!dag[stepId]) {
    return dag;
  }
  const step = { ...dag[stepId] };

  const isFolder = step?.type === 'folder';
  const parentStepId = getParentStepId(stepId, dag);

  if (isFolder && shallow !== true) {
    return deleteFolderStep({ stepId, parentStepId, dag });
  }

  return deleteNormalStep({ stepId, parentStepId, dag, shallow, keepChildren });
};
